import { memo, useState, useCallback } from 'react';
import { z } from 'zod';
import {
  useForm,
  zodResolver,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Button,
} from '@flote/ui';
import { Lock, CheckCircle2, Info, AlertCircle } from 'lucide-react';
import { useFunnelTracking } from '@flote/app-core';

const bankAccountFormSchema = z
  .object({
    accountName: z
      .string()
      .min(2, { message: 'Account name must be at least 2 characters' })
      .max(100, { message: 'Account name must be less than 100 characters' }),
    routingNumber: z.string().regex(/^\d{9}$/, {
      message: 'Routing number must be exactly 9 digits',
    }),
    accountNumber: z
      .string()
      .min(4, { message: 'Account number must be at least 4 digits' })
      .max(17, { message: 'Account number must be less than 17 digits' })
      .regex(/^\d+$/, { message: 'Account number must contain only digits' }),
    accountNumberConfirm: z.string(),
    accountType: z.enum(['checking', 'savings'], {
      required_error: 'Please select an account type',
    }),
    isDefault: z.boolean(),
    termsAccepted: z.boolean().refine(val => val === true, {
      message: 'You must accept the ACH authorization to continue',
    }),
  })
  .refine(data => data.accountNumber === data.accountNumberConfirm, {
    message: "Account numbers don't match",
    path: ['accountNumberConfirm'],
  });

type BankAccountFormValues = z.infer<typeof bankAccountFormSchema>;

export interface BankAccountFormData {
  accountName: string;
  routingNumber: string;
  accountNumber: string;
  accountType: 'checking' | 'savings';
  isDefault: boolean;
}

interface BankAccountFormProps {
  onSubmit: (data: BankAccountFormData) => Promise<void>;
  isProcessing: boolean;
}

export const BankAccountForm = memo<BankAccountFormProps>(
  ({ onSubmit, isProcessing: externalIsProcessing }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { trackBankAccountFormSubmitted } = useFunnelTracking();

    const form = useForm<BankAccountFormValues>({
      resolver: zodResolver(bankAccountFormSchema),
      mode: 'onChange',
      defaultValues: {
        accountName: '',
        routingNumber: '',
        accountNumber: '',
        accountNumberConfirm: '',
        accountType: 'checking',
        isDefault: false,
        termsAccepted: false,
      },
    });

    const handleSubmit = useCallback(
      async (data: BankAccountFormValues) => {
        const { accountNumberConfirm: _, termsAccepted: __, ...bankAccountData } = data;
        trackBankAccountFormSubmitted({
          accountType: bankAccountData.accountType,
          isDefault: bankAccountData.isDefault,
        });
        setIsSubmitting(true);
        try {
          await onSubmit(bankAccountData);
        } finally {
          setIsSubmitting(false);
        }
      },
      [onSubmit, trackBankAccountFormSubmitted]
    );

    const isProcessing = isSubmitting || externalIsProcessing;

    return (
      <div className="space-y-6 p-4 sm:p-6">
        {/* Security Disclaimer */}
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="flex gap-3">
            <Lock className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
            <div className="space-y-2 text-sm">
              <p className="font-medium text-blue-900">Your information is secure</p>
              <ul className="space-y-1.5 text-blue-800">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <span>
                    Bank details are encrypted using industry-standard 256-bit SSL/TLS encryption
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <span>
                    Data is securely tokenized and stored with our PCI DSS Level 1 compliant payment
                    processor
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <span>We never store your complete account number in plain text</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <span>All transmissions are protected by secure HTTPS connections</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
            {/* Account Name */}
            <FormField
              control={form.control}
              name="accountName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">Account Holder Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="John Doe or ABC Company LLC"
                      {...field}
                      disabled={isProcessing}
                      className="h-11"
                      autoComplete="off"
                    />
                  </FormControl>
                  <FormDescription className="text-xs">
                    Name as it appears on your bank account
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Account Type */}
            <FormField
              control={form.control}
              name="accountType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">Account Type</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      disabled={isProcessing}
                      className="flex gap-4 pt-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="checking" id="checking" />
                        <Label htmlFor="checking" className="cursor-pointer font-normal">
                          Checking
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="savings" id="savings" />
                        <Label htmlFor="savings" className="cursor-pointer font-normal">
                          Savings
                        </Label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Routing Number */}
            <FormField
              control={form.control}
              name="routingNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">Routing Number</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="123456789"
                      {...field}
                      disabled={isProcessing}
                      maxLength={9}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      className="h-11 font-mono tracking-wider"
                      autoComplete="off"
                      id='routing-number'
                    />
                  </FormControl>
                  <FormDescription className="flex items-start gap-1.5 text-xs">
                    <Info className="mt-0.5 h-3 w-3 flex-shrink-0" />
                    <span>9-digit number found on the bottom left of your check</span>
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Account Number */}
            <FormField
              control={form.control}
              name="accountNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">Account Number</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="••••••••••"
                      {...field}
                      disabled={isProcessing}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      className="h-11 font-mono tracking-wider"
                      autoComplete="off"
                      id='account-number'
                    />
                  </FormControl>
                  <FormDescription className="flex items-start gap-1.5 text-xs">
                    <Info className="mt-0.5 h-3 w-3 flex-shrink-0" />
                    <span>Found on the bottom center/right of your check</span>
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Confirm Account Number */}
            <FormField
              control={form.control}
              name="accountNumberConfirm"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">Confirm Account Number</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="••••••••••"
                      {...field}
                      disabled={isProcessing}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      className="h-11 font-mono tracking-wider"
                      autoComplete="off"
                      id='account-number-confirm'
                    />
                  </FormControl>
                  <FormDescription className="text-xs">
                    Re-enter your account number to confirm
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Set as Default */}
            <FormField
              control={form.control}
              name="isDefault"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border bg-muted/30 p-4">
                  <FormControl>
                    <input
                      type="checkbox"
                      checked={field.value}
                      onChange={field.onChange}
                      disabled={isProcessing}
                      className="mt-0.5 h-4 w-4 cursor-pointer rounded border-gray-300 text-primary focus:ring-primary"
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="cursor-pointer text-sm font-medium">
                      Set as default payment method
                    </FormLabel>
                    <FormDescription className="text-xs">
                      Use this account for future orders automatically
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            {/* ACH Authorization Agreement */}
            <FormField
              control={form.control}
              name="termsAccepted"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <div className="rounded-lg border border-border bg-muted/20 p-4">
                    <div className="mb-3 flex gap-2">
                      <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
                      <p className="text-xs font-semibold text-muted-foreground">
                        ACH Authorization Agreement
                      </p>
                    </div>
                    <div className="space-y-2.5 text-xs leading-relaxed text-muted-foreground">
                      <p>
                        By providing your bank account information and selecting "Add Bank Account,"
                        you authorize <strong>Flote</strong> and our payment processor,{' '}
                        <strong>ACH Processing Company</strong>, to electronically debit your
                        designated bank account for purchases made through this service.
                      </p>
                      <p>
                        You agree that ACH (Automated Clearing House) transactions will be processed
                        in accordance with applicable NACHA (National Automated Clearing House
                        Association) operating rules and regulations. This authorization will remain
                        in effect until you revoke it by contacting our customer support team.
                      </p>
                      <p>
                        <strong>Data Security:</strong> Your bank account information is encrypted
                        during transmission and securely tokenized by our PCI DSS Level 1 compliant
                        payment processor. We do not store your complete account number.
                      </p>
                      <p>
                        <strong>Transaction Processing:</strong> ACH debits will be initiated at the
                        time of order placement or as otherwise agreed. Processing typically takes
                        1-3 business days.
                      </p>
                      <p>
                        <strong>Revocation Rights:</strong> You may revoke this authorization at any
                        time by contacting us at least 3 business days before the next scheduled
                        transaction. Revocation must be submitted in writing via email or through
                        your account settings.
                      </p>
                      <p>
                        <strong>Disputes:</strong> If you believe an ACH debit was processed in
                        error or was unauthorized, you must notify us within 60 days of the
                        transaction date. You may also have rights under your bank's dispute
                        resolution procedures.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        disabled={isProcessing}
                        className="mt-0.5 h-4 w-4 cursor-pointer rounded border-gray-300 text-primary focus:ring-primary"
                      />
                    </FormControl>
                    <div className="flex-1 space-y-1 leading-none">
                      <FormLabel className="cursor-pointer text-sm font-medium">
                        I have read and agree to the ACH Authorization Agreement
                      </FormLabel>
                      <FormMessage />
                    </div>
                  </div>
                </FormItem>
              )}
            />

            {/* Action Button */}
            <div className="flex pt-2">
              <Button
                type="submit"
                disabled={isProcessing || !form.formState.isValid}
                className="w-full"
              >
                {isProcessing ? (
                  <>
                    <span className="mr-2">Processing...</span>
                    <span className="animate-spin">⏳</span>
                  </>
                ) : (
                  <>
                    <Lock className="mr-2 h-4 w-4" />
                    Add Bank Account
                  </>
                )}
              </Button>
            </div>
          </form>
        </Form>

        {/* Help Text */}
        <div className="border-t border-border pt-2">
          <p className="text-center text-xs text-muted-foreground">
            Need help finding your routing and account numbers?{' '}
            <a
              href="https://www.investopedia.com/how-to-find-routing-number-4783188"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-primary hover:underline"
            >
              View guide
            </a>
          </p>
        </div>
      </div>
    );
  }
);

BankAccountForm.displayName = 'BankAccountForm';
